import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { config } from './config';

// Global Application Insights instance
let appInsights: ApplicationInsights | null = null;

// Set authenticated user context
const setAuthenticatedUser = (): void => {
  if (!appInsights) return;
  try {
    // Thử lấy userId từ localStorage hoặc sessionStorage
    const userId =
      localStorage.getItem('userId') ||
      localStorage.getItem('user_id') ||
      sessionStorage.getItem('userId') ||
      sessionStorage.getItem('user_id');

    if (userId) {
      appInsights.setAuthenticatedUserContext(userId);
      console.log('Application Insights: Authenticated user context set', userId);
    }
  } catch (error) {
    console.warn('Failed to set authenticated user context:', error);
  }
};

// Override console.error để track vào App Insights
const setupConsoleErrorTracking = (): void => {
  if (!appInsights) return;

  const originalConsoleError = console.error;

  console.error = (...args: any[]) => {
    originalConsoleError.apply(console, args);
    try {
      const errorMessage = args
        .map((arg) => (typeof arg === 'string' ? arg : JSON.stringify(arg)))
        .join(' ');
      appInsights?.trackTrace({
        message: `Console Error: ${errorMessage}`,
        severityLevel: 3,
      });
    } catch (error) {
      originalConsoleError('Failed to track console error to App Insights:', error);
    }
  };
};

// Setup global error handlers
const setupGlobalErrorHandlers = (): void => {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    if (appInsights) {
      appInsights.trackException({
        exception: new Error(`Unhandled Promise Rejection: ${event.reason}`),
        properties: {
          type: 'unhandledrejection',
          reason: event.reason?.toString(),
        },
      });
    }
  });

  // Handle global runtime errors
  window.addEventListener('error', (event) => {
    if (appInsights) {
      appInsights.trackException({
        exception: event.error || new Error(event.message),
        properties: {
          type: 'runtime_error',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    }
  });
};

// Khởi tạo Application Insights
export const initAppInsights = (): void => {
  const connectionString = config.monitoring.appInsightsConnectionString;

  if (!connectionString) {
    console.warn('Application Insights: Connection string not found in configuration');
    return;
  }
  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
        disableFetchTracking: false,
        disableXhr: false,
        autoTrackPageVisitTime: true,
        enableCorsCorrelation: true,
        disableExceptionTracking: false,
      },
    });

    const telemetryInitializer = (envelope: any) => {
      envelope.tags['ai.cloud.role'] = 'denise-web';
      envelope.tags['ai.cloud.roleInstance'] = 'denise-web';
    };

    appInsights.addTelemetryInitializer(telemetryInitializer);

    // Load Application Insights
    appInsights.loadAppInsights();

    // Set authenticated user context nếu có
    setAuthenticatedUser();

    // Override console.error để track vào App Insights
    setupConsoleErrorTracking();

    // Setup global error handlers
    setupGlobalErrorHandlers();
  } catch (error) {
    console.error('Failed to initialize Application Insights:', error);
  }
};
// Utility function để log custom events
export const logEvent = (
  name: string,
  properties?: Record<string, any>,
  measurements?: Record<string, number>,
): void => {
  if (!appInsights) {
    console.warn('Application Insights not initialized');
    return;
  }

  try {
    appInsights.trackEvent({
      name,
      properties,
      measurements,
    });
  } catch (error) {
    console.error('Failed to log event to Application Insights:', error);
  }
};

// Utility function để log custom errors
export const logError = (error: Error | string, properties?: Record<string, any>): void => {
  if (!appInsights) {
    console.warn('Application Insights not initialized');
    return;
  }

  try {
    const exception = typeof error === 'string' ? new Error(error) : error;

    appInsights.trackException({
      exception,
      properties: {
        ...properties,
        customError: true,
      },
    });
  } catch (err) {
    console.error('Failed to log error to Application Insights:', err);
  }
};

// Utility function để log custom traces
export const logTrace = (
  message: string,
  severityLevel: 0 | 1 | 2 | 3 | 4 = 1,
  properties?: Record<string, any>,
): void => {
  if (!appInsights) {
    console.warn('Application Insights not initialized');
    return;
  }

  try {
    appInsights.trackTrace({
      message,
      severityLevel,
      properties,
    });
  } catch (error) {
    console.error('Failed to log trace to Application Insights:', error);
  }
};

// Function để track page views manually nếu cần
export const trackPageView = (name?: string, uri?: string): void => {
  if (!appInsights) {
    console.warn('Application Insights not initialized');
    return;
  }

  try {
    appInsights.trackPageView({
      name,
      uri,
    });
  } catch (error) {
    console.error('Failed to track page view to Application Insights:', error);
  }
};

// Function để update user context khi user login/logout
export const updateUserContext = (userId?: string): void => {
  if (!appInsights) {
    console.warn('Application Insights not initialized');
    return;
  }

  try {
    if (userId) {
      appInsights.setAuthenticatedUserContext(userId);
      // Cập nhật localStorage để dùng cho lần khởi tạo tiếp theo
      localStorage.setItem('userId', userId);
    } else {
      appInsights.clearAuthenticatedUserContext();
      localStorage.removeItem('userId');
    }
  } catch (error) {
    console.error('Failed to update user context:', error);
  }
};

// Function để flush telemetry (hữu ích khi app sắp đóng)
export const flushTelemetry = (): void => {
  if (!appInsights) return;

  try {
    appInsights.flush();
  } catch (error) {
    console.error('Failed to flush telemetry:', error);
  }
};

export const getAppInsights = (): ApplicationInsights | null => appInsights;

// Function test để kiểm tra các tính năng của Application Insights
export const testAppInsights = (): void => {
  console.log('Testing Application Insights features...');

  // Test 1: Log một custom event
  setTimeout(() => {
    logEvent('Test_Custom_Event', {
      testProperty: 'test_value',
      timestamp: new Date().toISOString(),
    });
    console.log('✅ Test custom event đã được gửi');
  }, 1000);

  // Test 2: Log một custom error
  setTimeout(() => {
    logError('Đây là một lỗi test từ Application Insights', {
      testType: 'manual_test',
      component: 'appInsights_test',
    });
    console.log('✅ Test custom error đã được gửi');
  }, 2000);

  // Test 3: Test console.error tracking
  setTimeout(() => {
    console.error('Đây là test console.error sẽ được track vào App Insights');
    console.log('✅ Test console.error tracking đã được gửi');
  }, 3000);

  // Test 4: Test trace logging
  setTimeout(() => {
    logTrace('Test trace message từ Application Insights', 1, {
      testCategory: 'information',
      source: 'manual_test',
    });
    console.log('✅ Test trace đã được gửi');
  }, 4000);

  console.log(
    'All Application Insights tests scheduled. Check Azure Portal để xem telemetry data.',
  );
};

export default {
  initAppInsights,
  logEvent,
  logError,
  logTrace,
  trackPageView,
  updateUserContext,
  flushTelemetry,
  getAppInsights,
  testAppInsights,
};
